import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { GET_AUDIENCE_HYPOTHESIS_LABELS } from '@/graphql/audience-hypothesis-queries'
import { toTargetingOptions } from '@/utils/audience-builder'

const NO_LABELS = {}

/**
 * Looks up the labels of the AI audiences used by the given ad sets. Nothing is requested when no ad set uses one.
 * A failed lookup is not an error for the page: the ad sets keep their own names.
 */
export function useHypothesisLabels(adSets) {
    const { id: campaignId } = useParams()
    const ids = useMemo(
        () => [...new Set((adSets || []).map((adSet) => adSet.audience_hypothesis_id).filter(Boolean))],
        [adSets],
    )

    const { data, loading } = useQuery(GET_AUDIENCE_HYPOTHESIS_LABELS, {
        variables: { campaignId, ids },
        skip: !campaignId || ids.length === 0,
    })

    const labels = useMemo(() => {
        const rows = data?.AudienceHypothesis?.audienceHypotheses
        if (!rows) return NO_LABELS
        return Object.fromEntries(
            rows.map((row) => [
                row.id,
                { label: row.label, kind: row.kind, targetingOptions: toTargetingOptions(row.targeting_options) },
            ]),
        )
    }, [data])

    return { labels, loading: ids.length > 0 && loading }
}
